import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useT } from '../../providers/I18nProvider';

export default function DropdownProfile() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const trigger = useRef(null);
  const dropdown = useRef(null);
  const navigate = useNavigate();
  const t = useT();

  const userinfo = JSON.parse(localStorage.getItem('userinfo') || '{}');
  const role = localStorage.getItem('role') || 'user';
  const user_avatar = localStorage.getItem('user_avatar');

  // Close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (!dropdownOpen || dropdown.current.contains(target) || trigger.current.contains(target)) return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, [dropdownOpen]);

  // Close if esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [dropdownOpen]);

  const getUserAvatar = () => {
    if (!user_avatar) return null;
    if (user_avatar.startsWith('data:')) return user_avatar;
    return `data:image/jpeg;base64,${user_avatar}`;
  };

  const getInitials = () => {
    const firstName = userinfo?.first_name || '';
    const lastName = userinfo?.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  return (
    <div className="relative inline-flex">
      <button
        ref={trigger}
        className="inline-flex justify-center items-center group"
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        {getUserAvatar() ? (
          <img 
            className="w-10 h-10 rounded-full object-cover ring-2 ring-eucnet-grey-200 hover:ring-eucnet-sky transition-all" 
            src={getUserAvatar()}
            alt="User" 
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-eucnet-blue text-white flex items-center justify-center font-semibold text-sm ring-2 ring-eucnet-grey-200 hover:ring-eucnet-sky transition-all">
            {getInitials()}
          </div>
        )}
        <svg className="w-3 h-3 shrink-0 ml-2 fill-current text-eucnet-grey-600" viewBox="0 0 12 12">
          <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
        </svg>
      </button>

      {dropdownOpen && (
        <div className="origin-top-right z-50 absolute top-full right-0 min-w-56 bg-white border border-eucnet-grey-200 rounded-lg shadow-xl overflow-hidden mt-2">
          <div
            ref={dropdown}
          >
            {/* User Info */}
            <div className="pt-3 pb-3 px-4 border-b border-eucnet-grey-200 bg-eucnet-grey-100">
              <div className="text-sm font-semibold text-eucnet-navy">
                {userinfo.first_name} {userinfo.last_name}
              </div>
              <div className="text-xs text-eucnet-grey-600 mt-0.5">
                {role === 'global_admin' ? t('roles.globalAdmin') :
                 role === 'assessment_admin' ? t('roles.assessmentAdmin') :
                 role === 'admin' ? t('roles.administrator') :
                 t('roles.user')}
              </div>
            </div>

            {/* Menu Items */}
            <ul className="py-1">
              <li>
                <Link
                  className="flex items-center px-4 py-2.5 text-sm text-eucnet-grey-600 hover:bg-eucnet-grey-100 hover:text-eucnet-blue transition-colors"
                  to="/profile"
                  onClick={() => setDropdownOpen(false)}
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {t('nav.myProfile')}
                </Link>
              </li>
              {(role === 'global_admin' || role === 'assessment_admin' || role === 'admin') && (
                <li>
                  <Link
                    className="flex items-center px-4 py-2.5 text-sm text-eucnet-grey-600 hover:bg-eucnet-grey-100 hover:text-eucnet-blue transition-colors"
                    to="/admin"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {t('nav.adminPanel')}
                  </Link>
                </li>
              )}
              <li className="border-t border-eucnet-grey-200 mt-1 pt-1">
                <button
                  className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  onClick={handleLogout}
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  {t('auth.signOut')}
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
